import { useEffect, useRef, useState } from "react";
import { Modal } from "../../../components/common/Modal/Modal";
import { ModalHeader } from "../../../components/common/Modal/ModalHeader";
import { ModalBody } from "../../../components/common/Modal/ModalBody";
import { ModalFooter } from "../../../components/common/Modal/ModalFooter";
import { Formik } from "formik";
import { useNoticeStore } from "../../../hooks/useNoticeStore";
import { NoticeCreateEdit } from "../../../types/slices/noticesType";
import { Box, Chip, FormLabel, Grid, MenuItem, TextField } from "@mui/material";

const today = (() => {
  const peruNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Lima" }),
  );
  peruNow.setDate(peruNow.getDate() - 1);
  return peruNow.toISOString().split("T")[0];
})();

const MAX_SIZE_MB = 5;

export const CreateEditNoticeModal: React.FC<any> = (): JSX.Element | any => {
  const tagInputRef = useRef<HTMLInputElement>(null);
  const {
    editNotice,
    openModal,
    setOpenModal,
    isEdit,
    createNoticeStore,
    editNoticeStore,
    getNotices,
  } = useNoticeStore();
  const [data, setData] = useState<NoticeCreateEdit>({
    title: "",
    content: "",
    image: "",
    author: "",
    date: today,
    tags: [],
    order: 1,
    status: "1",
  });

  const validateImage = (file: File): Promise<string | null> => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      return Promise.resolve("Formato inválido. Solo JPG o PNG");
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return Promise.resolve("La imagen no debe superar 5MB");
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const { width, height } = img;
          const aspectRatio = width / height;
          if (width < 800 || height < 450) {
            resolve("La imagen debe tener al menos 800x450 px");
            return;
          }
          if (aspectRatio < 0.75 || aspectRatio > 2.5) {
            resolve("La imagen no tiene un formato válido para noticia");
            return;
          }
          resolve(null);
        };
        img.onerror = () => {
          resolve("No se pudo validar la imagen");
        };
        img.src = reader.result as string;
      };
      reader.onerror = () => {
        resolve("No se pudo leer el archivo de imagen");
      };
      reader.readAsDataURL(file);
    });
  };

  const validateForm = (values) => {
    let errors: any = {};
    if (!values.title) errors.title = "Titulo es requerido";
    if (values.title && values.title.length > 200)
      errors.title = "El número máximo de caracteres es 200";
    if (!values.content) errors.content = "El contenido es requerido";
    if (values.content && values.content.length < 20)
      errors.content = "El contenido debe tener al menos 20 caracteres";
    if (!values.author) errors.author = "El autor es requerido";
    if (!values.date) errors.date = "La fecha es requerida";
    if (!values.tags) errors.tags = "Los tags son requeridos";
    if (values.tags.length < 1) errors.tags = "Debe ingresar al menos un tag";
    if (!values.order) errors.order = "El orden es requerido";
    if (values.order < 1) errors.order = "El orden debe ser mayor o igual a 1";
    if (!isEdit && !values.image) {
      errors.image = "La imagen es obligatoria";
    }
    return errors;
  };

  const onSubmit = async (values) => {
    const method = isEdit
      ? editNoticeStore(editNotice?.id || 0, values)
      : createNoticeStore(values);
    const response = await method;
    if (response === true) {
      getNotices();
      setOpenModal(false);
    }
  };

  const clear = () => {
    setData({
      title: "",
      content: "",
      image: "",
      author: "",
      date: today,
      tags: [],
      order: 1,
      status: "1",
    });
  };

  useEffect(() => {
    if (isEdit && openModal) {
      setData(editNotice);
    } else {
      setData({
        title: "",
        content: "",
        image: "",
        author: "",
        date: today,
        tags: [],
        order: 1,
        status: "1",
      });
    }
  }, [isEdit, editNotice, openModal]);

  return (
    <>
      <Modal
        open={openModal}
        handleClose={() => {
          clear();
          setOpenModal(false);
        }}
        disableEscapeKeyDown
        disableBackdropClick
        size="sm"
      >
        <ModalHeader
          text={isEdit ? "Editar Noticia" : "Crear Noticia"}
          className="positionElements"
          onCancel={() => {
            clear();
            setOpenModal(false);
          }}
        ></ModalHeader>

        <Formik
          initialValues={data}
          enableReinitialize
          validate={(values) => validateForm(values)}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            handleChange,
            setFieldValue,
            setFieldError,
          }) => {
            const preview = values.image
              ? typeof values.image === "string"
                ? values.image
                : URL.createObjectURL(values.image)
              : null;
            return (
              <>
                <ModalBody>
                  <form onSubmit={handleSubmit}>
                    <Grid container>
                      <Grid item container xs={12} spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                          <FormLabel>Titulo:</FormLabel>
                          <TextField
                            id="title"
                            type="text"
                            name="title"
                            fullWidth
                            size="small"
                            value={values.title}
                            onChange={handleChange}
                            error={errors.title && touched.title ? true : false}
                            helperText={
                              errors.title && touched.title ? errors.title : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Contenido:</FormLabel>
                          <TextField
                            name="content"
                            multiline
                            minRows={8}
                            fullWidth
                            size="small"
                            placeholder="Escribe el contenido de la noticia..."
                            value={values.content}
                            onChange={handleChange}
                            error={touched.content && Boolean(errors.content)}
                            helperText={touched.content && errors.content}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Autor:</FormLabel>
                          <TextField
                            id="author"
                            type="text"
                            name="author"
                            fullWidth
                            size="small"
                            value={values.author}
                            onChange={handleChange}
                            error={
                              errors.author && touched.author ? true : false
                            }
                            helperText={
                              errors.author && touched.author
                                ? errors.author
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Fecha:</FormLabel>
                          <TextField
                            id="date"
                            type="date"
                            name="date"
                            fullWidth
                            size="small"
                            value={values.date}
                            onChange={handleChange}
                            error={errors.date && touched.date ? true : false}
                            helperText={
                              errors.date && touched.date ? errors.date : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Tags</FormLabel>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              border: "1px solid #ccc",
                              borderRadius: 1,
                              padding: 1,
                            }}
                          >
                            {values.tags.map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                onDelete={() =>
                                  setFieldValue(
                                    "tags",
                                    values.tags.filter((_, i) => i !== index),
                                  )
                                }
                              />
                            ))}
                            <TextField
                              inputRef={tagInputRef}
                              size="small"
                              variant="standard"
                              fullWidth
                              placeholder="Escribe un tag y presiona Enter"
                              sx={{ minWidth: 150 }}
                              onKeyDown={(e) => {
                                if (e.key !== "Enter") return;
                                e.preventDefault();
                                const value =
                                  tagInputRef.current?.value?.trim();
                                if (!value) return;
                                if (values.tags.includes(value)) return;
                                setFieldValue("tags", [...values.tags, value]);
                                tagInputRef.current!.value = "";
                              }}
                            />
                          </Box>
                          {errors.tags && touched.tags && (
                            <Box sx={{ color: "error.main", fontSize: 12 }}>
                              {errors.tags}
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Orden:</FormLabel>
                          <TextField
                            id="order"
                            name="order"
                            type="number"
                            fullWidth
                            size="small"
                            inputProps={{ min: 1 }}
                            value={values.order || 1}
                            onChange={handleChange}
                            error={errors.order && touched.order ? true : false}
                            helperText={
                              errors.order && touched.order ? errors.order : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Imagen:</FormLabel>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={8}>
                              <TextField
                                id="image"
                                name="image"
                                type="file"
                                fullWidth
                                size="small"
                                inputProps={{
                                  accept: "image/png, image/jpeg, image/jpg, image/webp",
                                }}
                                onChange={async (
                                  event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  const file = event.target.files?.[0];
                                  if (!file) return;
                                  const error = await validateImage(file);
                                  if (error) {
                                    setFieldError("image", error);
                                    return;
                                  }
                                  setFieldValue("image", file);
                                }}
                                error={Boolean(errors.image && touched.image)}
                                helperText={
                                  errors.image && touched.image
                                    ? errors.image
                                    : ""
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              {preview ? (
                                <img
                                  src={preview}
                                  alt="Preview"
                                  loading="lazy"
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    objectFit: "cover",
                                    border: "1px solid #ddd",
                                    borderRadius: 4,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    border: "1px dashed #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#999",
                                    fontSize: 12,
                                  }}
                                >
                                  Sin imagen
                                </div>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Estado:</FormLabel>
                          <TextField
                            select
                            id="status"
                            name="status"
                            fullWidth
                            size="small"
                            value={values.status}
                            onChange={handleChange}
                            error={
                              errors.status && touched.status ? true : false
                            }
                            helperText={
                              errors.status && touched.status
                                ? errors.status
                                : ""
                            }
                          >
                            <MenuItem value="1">Activo</MenuItem>
                            <MenuItem value="0">Inactivo</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                </ModalBody>
                <ModalFooter
                  confirmText={isEdit ? "Actualizar" : "Registrar"}
                  onConfirm={handleSubmit}
                  cancelText={"Cancelar"}
                  onCancel={() => {
                    setOpenModal(false);
                  }}
                  className="modal-confirm-footer"
                  // loadingConfirmText={status2}
                />
              </>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};
